'use client';

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin, X, CalendarMinus2, ClockFading } from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { CATEGORIES } from '../../../constants/categories';
import redirectTo from '../../../utils/redirect';
import { slugify } from '../../../utils/slugify';
import { AddToCalendarModal } from '../../events/components/AddToCalendarModal';

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  image?: string;
  category: string;
  date: string; // YYYY-MM-DD format
  time: string; // HH:MM format
  endDate?: string; // YYYY-MM-DD format
  endTime?: string; // HH:MM format
  location: string;
  eventType?: string;
  modality?: 'in-person' | 'online' | 'hybrid';
  type?: 'hosted' | 'attending';
  hostStatus?: 'pending' | 'accepted' | 'declined';
}

interface CalendarViewProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
}



const getModalityIcon = (modality?: string) => {
  switch (modality) {
    case 'online':
      return '🌐';
    case 'hybrid':
      return '🔄';
    case 'in-person':
    default:
      return '📍';
  }
};

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const CalendarView: React.FC<CalendarViewProps> = ({ events, onEventClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const formatEventDate = (dateString?: string) => {
    if (!dateString) return 'Date TBA';
    // Parsear YYYY-MM-DD como fecha local para evitar desfase por UTC
    const base = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const [y, m, d] = base.split('-').map(Number);
    const date = (y && m && d) ? new Date(y, m - 1, d) : new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatEventTime = (timeString?: string) => {
    if (!timeString) return 'Time TBA';
    try {
      const [hours, minutes] = timeString.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return timeString;
    }
  };

  const calculateEndTime = (startTime: string): string => {
    try {
      const [hours, minutes] = startTime.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes);
      date.setHours(date.getHours() + 2);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    } catch (e) {
      return '00:00';
    }
  };

  const parseTimeToMinutes = (timeString?: string): number => {
    if (!timeString) return Number.POSITIVE_INFINITY;
    const normalized = timeString.trim().toUpperCase();
    if (normalized === 'TBD') return Number.POSITIVE_INFINITY;
    const parts = timeString.split(':');
    if (parts.length < 2) return Number.POSITIVE_INFINITY;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return Number.POSITIVE_INFINITY;
    return hours * 60 + minutes;
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Convert Sunday (0) to 6, Monday (1) to 0, etc.
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const formatDateForComparison = (year: number, month: number, day: number): string => {
    // Generar YYYY-MM-DD en local sin pasar por UTC
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  const getEventsForDay = (day: number | null) => {
    if (!day) return [];
    
    const dateStr = formatDateForComparison(
      currentDate.getFullYear(), 
      currentDate.getMonth(), 
      day
    );
    
    const dayEvents = events.filter(event => {
      const eventDate = event.date.includes('T') ? event.date.split('T')[0] : event.date;
      return eventDate === dateStr;
    });

    dayEvents.sort((a, b) => {
      const diff = parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time);
      if (diff !== 0) return diff;
      return a.title.localeCompare(b.title);
    });

    return dayEvents;
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setIsModalOpen(true);
    if (onEventClick) {
      onEventClick(event);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const calendarDays = generateCalendarDays();

  return (
    <>
      <Card className="p-3 sm:p-6 bg-white">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <button
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
          
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            <span className="hidden sm:inline">
              {monthNames[currentDate.getMonth()]} - {currentDate.getFullYear()}
            </span>
            <span className="sm:hidden">
              {monthNames[currentDate.getMonth()].substring(0, 3)} {currentDate.getFullYear()}
            </span>
          </h2>
          
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 gap-0">
          {dayNames.map((day) => (
            <div 
              style={{background: 'rgb(199, 200, 202)'}} 
              key={day} 
              className="p-1 sm:p-3 text-center text-xs sm:text-sm font-medium text-gray-500 bg-gray-50"
            >
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{day.substring(0, 1)}</span>
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDay(day);
            
            return (
              <div
                key={index}
                className="min-h-[60px] sm:min-h-[100px] lg:min-h-[120px] p-1 sm:p-2 border border-gray-200 bg-white relative"
              >
                {day && (
                  <>
                    {/* Day number */}
                    <div className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                      {day}
                    </div>
                    
                    {/* Events for this day */}
                    <div className="space-y-1">
                      {/* Mobile: Solo 1 evento */}
                      <div className="block sm:hidden">
                        {dayEvents.slice(0, 1).map((event, eventIndex) => {
                          const categoryInfo = CATEGORIES.find(cat => cat.name === event.category);
                          return (
                            <div
                              key={`${day}-${eventIndex}`}
                              onClick={(e) => handleEventClick(event, e)}
                              className="w-2 h-2 rounded-full cursor-pointer"
                              style={{
                                backgroundColor: categoryInfo?.color || '#6B7280'
                              }}
                              title={`${event.title} - ${event.time}`}
                            />
                          );
                        })}
                        {dayEvents.length > 1 && (
                          <div className="text-xs text-gray-500 text-center">
                            +{dayEvents.length - 1}
                          </div>
                        )}
                      </div>

                      {/* Tablet: 2 eventos */}
                      <div className="hidden sm:block lg:hidden">
                        {dayEvents.slice(0, 2).map((event, eventIndex) => {
                          const categoryInfo = CATEGORIES.find(cat => cat.name === event.category);
                          return (
                            <div
                              key={`${day}-${eventIndex}`}
                              onClick={(e) => handleEventClick(event, e)}
                              className="text-xs px-1 py-1 rounded border cursor-pointer hover:shadow-sm transition-shadow"
                              style={{
                                backgroundColor: categoryInfo?.color,
                                color: categoryInfo?.textColor,
                                borderColor: categoryInfo?.color
                              }}
                              title={`${event.title} - ${event.time}`}
                            >
                              <div className="truncate">{event.title.substring(0, 10)}...</div>
                            </div>
                          );
                        })}
                        {dayEvents.length > 2 && (
                          <div className="text-xs text-gray-500 text-center py-1">
                            +{dayEvents.length - 2}
                          </div>
                        )}
                      </div>

                      {/* Desktop: 3 eventos */}
                      <div className="hidden lg:block">
                        {dayEvents.slice(0, 3).map((event, eventIndex) => {
                          const categoryInfo = CATEGORIES.find(cat => cat.name === event.category);
                          return (
                            <div
                              key={`${day}-${eventIndex}`}
                              onClick={(e) => handleEventClick(event, e)}
                              className="text-xs px-2 py-1 rounded border cursor-pointer hover:shadow-sm transition-shadow"
                              style={{
                                backgroundColor: categoryInfo?.color,
                                color: categoryInfo?.textColor,
                                borderColor: categoryInfo?.color
                              }}
                              title={`${event.title} - ${event.time}`}
                            >
                              <div className="truncate">{event.title}</div>
                              {event.time !== 'TBD' && (
                                <div className="text-xs opacity-75">{event.time}</div>
                              )}
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 text-center py-1">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Event Modal - Make responsive */}
      {isModalOpen && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50" onClick={closeModal}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl relative max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Cerrar */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 z-10 p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
            <div className="flex-1 overflow-y-auto">
              {/* Imagen (resizing uniforme) */}
              <div className="w-full overflow-hidden rounded-t-2xl">
                <div className="aspect-video relative bg-gray-100">
                  {selectedEvent.image ? (
                    <img
                      src={selectedEvent.image}
                      alt={selectedEvent.title}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                      loading="lazy"
                      draggable={false}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
                      No image
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 sm:p-6">
                {/* Badge de categoría */}
                <div className="flex justify-end mb-2">
                  <span
                    className={`px-3 py-1 rounded-md text-xs font-semibold ${CATEGORIES.find(cat => cat.name === selectedEvent.category)?.color || 'bg-gray-200'} text-white`}
                  >
                    {selectedEvent.category}
                  </span>
                </div>

                {/* Título y descripción */}
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{selectedEvent.title}</h2>
                <p className="text-gray-600 text-sm mb-3">{selectedEvent.description}</p>

                {/* Organizador (si es un evento alojado) */}
                {selectedEvent.type === 'hosted' && (
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-gray-700 uppercase mr-2">Organizer:</span>
                    <span className="text-sm text-gray-800">{selectedEvent.hostStatus === 'accepted' ? 'You' : 'Pending'}</span>
                  </div>
                )}

                {/* Fecha y hora: inicio y fin */}
                <div className="flex flex-col gap-2 mb-5 text-gray-700 text-sm">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>from {formatEventDate(selectedEvent.date)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{formatEventTime(selectedEvent.time)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-1">
                      <CalendarMinus2 className="w-4 h-4" />
                      <span>to {formatEventDate(selectedEvent.endDate || selectedEvent.date)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ClockFading className="w-4 h-4" />
                      <span>{formatEventTime(selectedEvent.endTime || calculateEndTime(selectedEvent.time))}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {selectedEvent.location}
                  </div>
                </div>

                {/* Botones */}
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    className="border-2 border-purple-300 bg-purple-50 text-purple-700 flex items-center justify-center gap-2"
                    onClick={() => setShowCalendarModal(true)}
                  >
                    <Calendar className="w-4 h-4" />
                    Add to calendar
                  </Button>
                  <Button
                    variant='outline'
                    className="border border-gray-300"
                    onClick={() => redirectTo(`/event/${slugify(selectedEvent.title)}`)}
                  >
                    View details
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AddToCalendarModal */}
      {selectedEvent && showCalendarModal && (
        <AddToCalendarModal
          isOpen={showCalendarModal}
          onClose={() => setShowCalendarModal(false)}
          eventData={{
            id: selectedEvent.id,
            title: selectedEvent.title,
            description: selectedEvent.description,
            startDate: selectedEvent.date,
            startTime: selectedEvent.time,
            endTime: selectedEvent.endTime || calculateEndTime(selectedEvent.time),
            location: selectedEvent.location,
          }}
        />
      )}
    </>
  );
};